import { Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout.tsx'
import HomePage from './pages/HomePage.tsx'
import HubPage from './pages/HubPage.tsx'
import MethodologyPage from './pages/MethodologyPage.tsx'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.tsx'
import EnterpriseReportBuilder from './pages/EnterpriseReportBuilder.tsx'
import EnterpriseReportPage from './pages/EnterpriseReportPage.tsx'
import GlossaryPage from './pages/GlossaryPage.tsx'
import RolePage from './pages/RolePage.tsx'
import SkillPage from './pages/SkillPage.tsx'

const AISalaryGapCalculator = lazy(() => import('./calculators/AISalaryGapCalculator.tsx'))
const SkillsROICalculator = lazy(() => import('./calculators/SkillsROICalculator.tsx'))
const AIProductivityCalculator = lazy(() => import('./calculators/AIProductivityCalculator.tsx'))
const HireVsAICalculator = lazy(() => import('./calculators/HireVsAICalculator.tsx'))
const AutomationSavingsCalculator = lazy(() => import('./calculators/AutomationSavingsCalculator.tsx'))
const DisplacementRiskCalculator = lazy(() => import('./calculators/DisplacementRiskCalculator.tsx'))
const LifetimeEarningsCalculator = lazy(() => import('./calculators/LifetimeEarningsCalculator.tsx'))
const AITaxDeductionCalculator = lazy(() => import('./calculators/AITaxDeductionCalculator.tsx'))
const AIFreelanceRateCalculator = lazy(() => import('./calculators/AIFreelanceRateCalculator.tsx'))
const AISubscriptionROICalculator = lazy(() => import('./calculators/AISubscriptionROICalculator.tsx'))
const AIRaiseNegotiationCalculator = lazy(() => import('./calculators/AIRaiseNegotiationCalculator.tsx'))
const AISideHustleCalculator = lazy(() => import('./calculators/AISideHustleCalculator.tsx'))
const AIAPICostCalculator = lazy(() => import('./calculators/AIAPICostCalculator.tsx'))
const AIVsOffshoreCalculator = lazy(() => import('./calculators/AIVsOffshoreCalculator.tsx'))
const AITeamTrainingROICalculator = lazy(() => import('./calculators/AITeamTrainingROICalculator.tsx'))
const AIContentROICalculator = lazy(() => import('./calculators/AIContentROICalculator.tsx'))
const AIConsultingRateCalculator = lazy(() => import('./calculators/AIConsultingRateCalculator.tsx'))
const AIRevenueImpactCalculator = lazy(() => import('./calculators/AIRevenueImpactCalculator.tsx'))
const AIJobSearchCalculator = lazy(() => import('./calculators/AIJobSearchCalculator.tsx'))
const AIPricingElasticityCalculator = lazy(() => import('./calculators/AIPricingElasticityCalculator.tsx'))
const AIComplianceCostCalculator = lazy(() => import('./calculators/AIComplianceCostCalculator.tsx'))
const AITeamSizingCalculator = lazy(() => import('./calculators/AITeamSizingCalculator.tsx'))
const AIHallucinationCostCalculator = lazy(() => import('./calculators/AIHallucinationCostCalculator.tsx'))
const AISupportROICalculator = lazy(() => import('./calculators/AISupportROICalculator.tsx'))
const AIToolBudgetCalculator = lazy(() => import('./calculators/AIToolBudgetCalculator.tsx'))
const AIHealthcareAdminCalculator = lazy(() => import('./calculators/AIHealthcareAdminCalculator.tsx'))
const AIPortfolioExposureCalculator = lazy(() => import('./calculators/AIPortfolioExposureCalculator.tsx'))
const AILegalCostCalculator = lazy(() => import('./calculators/AILegalCostCalculator.tsx'))
const AIRecruitingROICalculator = lazy(() => import('./calculators/AIRecruitingROICalculator.tsx'))
const AIAdCreativeROICalculator = lazy(() => import('./calculators/AIAdCreativeROICalculator.tsx'))
const AICarbonFootprintCalculator = lazy(() => import('./calculators/AICarbonFootprintCalculator.tsx'))
const AICodingProductivityCalculator = lazy(() => import('./calculators/AICodingProductivityCalculator.tsx'))
const LocalVsCloudCalculator = lazy(() => import('./calculators/LocalVsCloudCalculator.tsx'))
const RagVsFineTuningCalculator = lazy(() => import('./calculators/RagVsFineTuningCalculator.tsx'))
const DataReadinessCalculator = lazy(() => import('./calculators/DataReadinessCalculator.tsx'))
const MultiAgentWorkflowCalculator = lazy(() => import('./calculators/MultiAgentWorkflowCalculator.tsx'))
const VoiceVideoCalculator = lazy(() => import('./calculators/VoiceVideoCalculator.tsx'))
const SaasVsCustomAgentCalculator = lazy(() => import('./calculators/SaasVsCustomAgentCalculator.tsx'))
const LeadResponseCalculator = lazy(() => import('./calculators/LeadResponseCalculator.tsx'))
const DataDegradationCalculator = lazy(() => import('./calculators/DataDegradationCalculator.tsx'))
const CyberPhishingCalculator = lazy(() => import('./calculators/CyberPhishingCalculator.tsx'))
const CareerTransitionCalculator = lazy(() => import('./calculators/CareerTransitionCalculator.tsx'))
const RtoVsNomadCalculator = lazy(() => import('./calculators/RtoVsNomadCalculator.tsx'))
const AICopyrightIPRiskCalculator = lazy(() => import('./calculators/AICopyrightIPRiskCalculator.tsx'))
const AIHardwareUpgradeROICalculator = lazy(() => import('./calculators/AIHardwareUpgradeROICalculator.tsx'))
const AIGigMarketplaceArbitrageCalculator = lazy(() => import('./calculators/AIGigMarketplaceArbitrageCalculator.tsx'))
const EmployeeAIExpenseReimbursementCalculator = lazy(() => import('./calculators/EmployeeAIExpenseReimbursementCalculator.tsx'))

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-pulse text-slate-500">Loading calculator...</div>
  </div>
)

function App() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/hub" element={<HubPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/enterprise-report-builder" element={<EnterpriseReportBuilder />} />
          <Route path="/enterprise-report" element={<EnterpriseReportPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/role/:roleId" element={<RolePage />} />
          <Route path="/skill/:skillId" element={<SkillPage />} />
        <Route path="/ai-salary-gap" element={<AISalaryGapCalculator />} />
        <Route path="/skills-roi" element={<SkillsROICalculator />} />
        <Route path="/ai-productivity" element={<AIProductivityCalculator />} />
        <Route path="/hire-vs-ai" element={<HireVsAICalculator />} />
        <Route path="/automation-savings" element={<AutomationSavingsCalculator />} />
        <Route path="/displacement-risk" element={<DisplacementRiskCalculator />} />
        <Route path="/lifetime-earnings" element={<LifetimeEarningsCalculator />} />
        <Route path="/ai-tax-deductions" element={<AITaxDeductionCalculator />} />
        <Route path="/ai-freelance-rate" element={<AIFreelanceRateCalculator />} />
        <Route path="/ai-subscription-roi" element={<AISubscriptionROICalculator />} />
        <Route path="/ai-raise-calculator" element={<AIRaiseNegotiationCalculator />} />
        <Route path="/ai-side-hustle" element={<AISideHustleCalculator />} />
        <Route path="/ai-api-cost" element={<AIAPICostCalculator />} />
        <Route path="/ai-vs-offshore" element={<AIVsOffshoreCalculator />} />
        <Route path="/ai-training-roi" element={<AITeamTrainingROICalculator />} />
        <Route path="/ai-content-roi" element={<AIContentROICalculator />} />
        <Route path="/ai-consulting-rate" element={<AIConsultingRateCalculator />} />
        <Route path="/ai-revenue-impact" element={<AIRevenueImpactCalculator />} />
        <Route path="/ai-job-search" element={<AIJobSearchCalculator />} />
        <Route path="/ai-pricing-elasticity" element={<AIPricingElasticityCalculator />} />
        <Route path="/ai-compliance-cost" element={<AIComplianceCostCalculator />} />
        <Route path="/ai-team-sizing" element={<AITeamSizingCalculator />} />
        <Route path="/ai-error-cost" element={<AIHallucinationCostCalculator />} />
        <Route path="/ai-support-roi" element={<AISupportROICalculator />} />
        <Route path="/ai-tool-budget" element={<AIToolBudgetCalculator />} />
        <Route path="/ai-healthcare-admin" element={<AIHealthcareAdminCalculator />} />
        <Route path="/ai-portfolio-exposure" element={<AIPortfolioExposureCalculator />} />
        <Route path="/ai-legal-cost" element={<AILegalCostCalculator />} />
        <Route path="/ai-recruiting-roi" element={<AIRecruitingROICalculator />} />
        <Route path="/ai-ad-creative-roi" element={<AIAdCreativeROICalculator />} />
        <Route path="/ai-carbon-cost" element={<AICarbonFootprintCalculator />} />
        <Route path="/ai-coding-productivity" element={<AICodingProductivityCalculator />} />
        <Route path="/local-vs-cloud-ai" element={<LocalVsCloudCalculator />} />
        <Route path="/rag-vs-fine-tuning" element={<RagVsFineTuningCalculator />} />
        <Route path="/data-readiness-cost" element={<DataReadinessCalculator />} />
        <Route path="/multi-agent-workflow" element={<MultiAgentWorkflowCalculator />} />
        <Route path="/ai-voice-video" element={<VoiceVideoCalculator />} />
        <Route path="/saas-vs-custom-agent" element={<SaasVsCustomAgentCalculator />} />
        <Route path="/lead-response-roi" element={<LeadResponseCalculator />} />
        <Route path="/data-degradation-saver" element={<DataDegradationCalculator />} />
        <Route path="/ai-cyber-risk" element={<CyberPhishingCalculator />} />
        <Route path="/ai-career-transition" element={<CareerTransitionCalculator />} />
        <Route path="/rto-vs-ai-nomad" element={<RtoVsNomadCalculator />} />
        <Route path="/ai-copyright-ip-risk" element={<AICopyrightIPRiskCalculator />} />
        <Route path="/ai-hardware-upgrade-roi" element={<AIHardwareUpgradeROICalculator />} />
        <Route path="/ai-gig-arbitrage" element={<AIGigMarketplaceArbitrageCalculator />} />
        <Route path="/employee-ai-reimbursement" element={<EmployeeAIExpenseReimbursementCalculator />} />
      </Routes>
      </Suspense>
    </Layout>
  )
}

export default App
